/**
 * The reading editor's formatting toolbar as pure text edits (spec screen 3, "rich text
 * editor"). A reading is stored in the safe Markdown subset of lib/readingMarkup.jsx,
 * never as HTML: each button writes that subset's syntax around the selection, so what
 * the author formats is exactly what the learner's renderer understands.
 *
 *   bold     **selection**
 *   italic   *selection*
 *   heading  "## " at the start of each selected line (removed when all already have it)
 *   bullets  "- " at the start of each selected line (removed when all already have it)
 *   link     [selection](https://) with the address selected, ready to be typed over
 *
 * Every function takes the textarea's value and selection and returns the new value and
 * the selection to restore, so the caret lands where the author expects to keep typing.
 */

export const READING_TOOLS = Object.freeze(["bold", "italic", "heading", "bullets", "link"]);

const LINK_PLACEHOLDER = "https://";

/** @returns {{ value: string, start: number, end: number }} */
function wrap(value, start, end, marker) {
  const selected = value.slice(start, end);
  const next = `${value.slice(0, start)}${marker}${selected}${marker}${value.slice(end)}`;
  return { value: next, start: start + marker.length, end: end + marker.length };
}

/** The [from, to) range of the whole lines the selection touches. */
function lineRange(value, start, end) {
  const from = value.lastIndexOf("\n", start - 1) + 1;
  const nl = value.indexOf("\n", end > start && value[end - 1] === "\n" ? end - 1 : end);
  return [from, nl < 0 ? value.length : nl];
}

function prefixLines(value, start, end, prefix) {
  const [from, to] = lineRange(value, start, end);
  const lines = value.slice(from, to).split("\n");
  const all = lines.every((l) => l.startsWith(prefix));
  const edited = lines.map((l) => (all ? l.slice(prefix.length) : `${prefix}${l}`)).join("\n");
  const next = `${value.slice(0, from)}${edited}${value.slice(to)}`;
  return { value: next, start: from, end: from + edited.length };
}

function link(value, start, end) {
  const label = value.slice(start, end);
  const inserted = `[${label}](${LINK_PLACEHOLDER})`;
  const next = `${value.slice(0, start)}${inserted}${value.slice(end)}`;
  if (!label) {
    // Nothing selected: the caret goes between the brackets, for the link's words.
    return { value: next, start: start + 1, end: start + 1 };
  }
  const urlAt = start + label.length + 3;
  return { value: next, start: urlAt, end: urlAt + LINK_PLACEHOLDER.length };
}

/**
 * @param {"bold"|"italic"|"heading"|"bullets"|"link"} tool
 * @param {string} value
 * @param {number} start  selectionStart
 * @param {number} end    selectionEnd
 * @returns {{ value: string, start: number, end: number }}
 */
export function applyReadingTool(tool, value, start, end) {
  const lo = Math.max(0, Math.min(start, end, value.length));
  const hi = Math.max(lo, Math.min(Math.max(start, end), value.length));
  if (tool === "bold") return wrap(value, lo, hi, "**");
  if (tool === "italic") return wrap(value, lo, hi, "*");
  if (tool === "heading") return prefixLines(value, lo, hi, "## ");
  if (tool === "bullets") return prefixLines(value, lo, hi, "- ");
  if (tool === "link") return link(value, lo, hi);
  return { value, start: lo, end: hi };
}
